import './ModulePositionManager.scss';
import { getIcon } from '@flybits/design-system';
import { TSlidingSidePanelProps } from 'components/ExperienceCanvas/types';
import { Zone } from 'pages/Zones/types';
import ModuleListItem from './ModuleListItem/ModuleListItem';
import PrioritizationContext from 'pages/Zones/PrioritizationContext/PrioritizationContext';
import React, { useCallback, useContext, useEffect, useMemo, useState, type JSX } from 'react';
import SlidingSidePanel from 'components/ExperienceCanvas/SlidingSidePanel/SlidingSidePanel';

type TModulePositionManager = {
  isOpen: boolean;
  onClose: () => void;
};

const MAIN_CLASS = 'module-position-manager';

const ModulePositionManager: React.FC<TModulePositionManager> = ({ isOpen, onClose }) => {
  const { selectedZone, updateZone } = useContext(PrioritizationContext);
  const [currentDraggingIndex, setCurrentDraggingIndex] = useState<number>();
  const [zone, setZone] = useState(selectedZone);

  useEffect(() => {
    if (isOpen && selectedZone) {
      setZone(selectedZone);
    }
  }, [isOpen, selectedZone]);

  const handleApply = useCallback(() => {
    updateZone(zone as Zone);
    onClose();
  }, [zone, updateZone, onClose]);

  const handleChangePosition = useCallback(
    (oldIndex: number, newIndex: number) => {
      const _modules = [...(zone?.modules || [])];
      if (
        oldIndex === newIndex ||
        oldIndex < 0 ||
        newIndex < 0 ||
        oldIndex >= _modules.length ||
        newIndex >= _modules.length
      ) {
        return;
      }
      const [moved] = _modules.splice(oldIndex, 1);
      _modules.splice(newIndex, 0, moved);

      setZone({ ...zone, modules: _modules } as Zone);
    },
    [zone],
  );

  const slidingSidePanelProps = useMemo<TSlidingSidePanelProps>(
    () => ({
      show: isOpen,
      headerInfo: {
        mainTitle: '',
        goBackIcon: getIcon('itemsList', {}) as JSX.Element,
        goBackTitle: 'Module Position',
        goBackSubTitle: 'Arrange the modules in this zone according to your preferred order',
        goBackActionHandler: onClose,
      },
      footerInfo: {
        primaryActionText: 'Apply',
        primaryActionHandler: handleApply,
        secondaryActionText: 'Cancel',
        secondaryActionHandler: onClose,
      },
      size: 'medium',
      className: MAIN_CLASS,
    }),
    [isOpen, onClose, handleApply],
  );

  return (
    <SlidingSidePanel {...slidingSidePanelProps}>
      <div>
        {zone?.modules?.map((module, index) => (
          <ModuleListItem
            key={module.id}
            index={index}
            listLength={zone.modules?.length || 0}
            name={module.name}
            onDragStart={setCurrentDraggingIndex}
            onDragEnd={() => setCurrentDraggingIndex(undefined)}
            onChangePosition={handleChangePosition}
            showDropzone={
              currentDraggingIndex !== undefined && currentDraggingIndex !== index && currentDraggingIndex !== index + 1
            }
          />
        )) || null}
      </div>
    </SlidingSidePanel>
  );
};

export default ModulePositionManager;
